import { useId, useState, type ReactNode } from 'react';
import { ChevronDown, Plus, Trash2, Pencil } from 'lucide-react';
import { Badge, Button, Input, Select, HelpTooltip } from '@fiado/react-kit';
import { Label } from '../../../../components/ui/label';
import { HelpHint } from '@/components/ui/HelpHint';
import { HELP_TEXT } from '@/lib/helpText';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Banner } from '../../../../components/ui/Banner';
// PROPUESTA AL KIT (DEC-RBAC-045): el kit aún NO exporta un Switch ni un Collapsible/Accordion. Se usa
// el Switch local (Radix) que ya existe en el repo y consume SecurityPolicyEditor, mismo precedente. La
// sección colapsable es el patrón `<button aria-expanded>` + ChevronDown de TenantDetail (markup
// trivial, sin primitivo de kit). Pendiente de OK de Andres para promover Switch (y eventualmente un
// Collapsible) a @fiado/react-kit y migrar estos usos.
import { Switch } from '../../../../components/ui/switch';
import type {
    ApplicationDefaultsDraft,
    ApplicationSecurityPolicyDefaultWire,
    AutoregisterAllowedRoleWire,
    AutoregisterConfigWire,
    LevelDefLocal,
    SeedRoleWire,
    TenantBrandingConfigWire,
    UserFieldDefDraft,
} from '../../../../api/types/ApplicationLocal';
import type { Scope } from '../../../../api/types/permissions';
import { TOKEN_VALIDATION_MODE_META } from '../../../../api/types/PlatformLocal';
import { MFA_METHODS } from '../../../../lib/securityPolicy';
import { OTHER_SCOPE_NAME, UNKNOWN_ROLE, WHOLE_ORGANIZATION } from '../../../../lib/labelConstants';

/* ───────────────────────── Defaults sanos (DEC-RBAC-047) ───────────────────────── */

// Política de seguridad por defecto: 2FA prendido, TOTP+EMAIL.
// DEC-RBAC-048: las reglas de contraseña y el timeout de sesión NO se configuran por aplicación
// (son estándar global; el enforcer del tenant aplica el default).
// DEC-RBAC-051: `mfaRequired`/`defaultPreferred` purgados del contrato (write-only sin enforcement).
const DEFAULT_SECURITY_POLICY: ApplicationSecurityPolicyDefaultWire = {
    mfaEnabled: true,
    methodsAllowed: ['TOTP', 'EMAIL'],
};

// DEC-RBAC-048: el TTL del OTP no se configura por app (estándar global).
const DEFAULT_AUTOREGISTER: AutoregisterConfigWire = {
    enabled: false,
    allowedRoles: [],
};

const MFA_METHOD_ORDER = ['TOTP', 'EMAIL', 'SMS'] as const;
type MfaMethod = (typeof MFA_METHOD_ORDER)[number];

function mfaMethodLabel(method: MfaMethod): string {
    return MFA_METHODS.find((m) => m.value === method)?.label ?? method;
}

// DEC-RBAC-085/087: los métodos OFRECIBLES dependen del identificador de acceso. TOTP siempre
// (agnóstico); el OTP del identificador PRINCIPAL siempre (todos los usuarios lo tienen); el OTP del
// SECUNDARIO solo si la app permite secundario (allowSecondary). Esto evita ofrecer un método que
// dejaría a usuarios sin identificador (invariante anti-lockout que el backend además valida).
function offerableMethods(
    principalIdentifier: 'EMAIL' | 'PHONE',
    allowSecondary: boolean,
): MfaMethod[] {
    const set = new Set<MfaMethod>(['TOTP']);
    set.add(principalIdentifier === 'PHONE' ? 'SMS' : 'EMAIL'); // OTP del principal (universal)
    if (allowSecondary) set.add(principalIdentifier === 'PHONE' ? 'EMAIL' : 'SMS'); // OTP del secundario
    return MFA_METHOD_ORDER.filter((m) => set.has(m));
}

/**
 * DEC-RBAC-087 (invariante anti-lockout): con 2FA prendido, `methodsAllowed` DEBE contener al menos un
 * método UNIVERSAL — TOTP (agnóstico) o el OTP del identificador PRINCIPAL (todos los usuarios lo tienen).
 * El OTP del SECUNDARIO no puede ser el único (dejaría afuera a los usuarios que no configuraron el
 * secundario). El backend además lo rechaza con `MFA_POLICY_NO_UNIVERSAL_METHOD`; el front lo previene.
 */
export function hasUniversalMfaMethod(
    security: ApplicationSecurityPolicyDefaultWire | undefined,
    principalIdentifier: 'EMAIL' | 'PHONE',
): boolean {
    if (!security?.mfaEnabled) return true; // 2FA apagado → no aplica el invariante
    const methods = security.methodsAllowed ?? [];
    const principalOtp: MfaMethod = principalIdentifier === 'PHONE' ? 'SMS' : 'EMAIL';
    return methods.includes('TOTP') || methods.includes(principalOtp);
}

const TOKEN_VALIDATION_MODES = ['offline', 'online_preferred', 'online_strict'] as const;
type TokenValidationMode = (typeof TOKEN_VALIDATION_MODES)[number];

// Misma regla que el catálogo de campos del tenant (UserFieldForm) — slug snake_case inmutable.
const FIELD_KEY_REGEX = /^[a-z][a-z0-9_]{1,49}$/;
type FieldType = UserFieldDefDraft['type'];

/* ─────────────────── Helper de coherencia del autoregistro (export) ─────────────────── */

/**
 * roleIds de `autoregister.allowedRoles` que NO corresponden a ningún seedRole de la app. El backend
 * rechaza esos con `AutoregisterRoleUnknownError`, así que el padre (wizard/detalle) usa este helper
 * para GATEAR el submit y el DefaultsStep para advertir inline. Devuelve la lista (vacía = todo OK).
 */
export function unknownAutoregisterRoleIds(
    autoregister: AutoregisterConfigWire | undefined,
    availableSeedRoles: SeedRoleWire[],
): string[] {
    if (!autoregister?.enabled) return [];
    const known = new Set(availableSeedRoles.map((r) => r.roleId));
    return autoregister.allowedRoles.map((r) => r.roleId).filter((roleId) => !known.has(roleId));
}

/** True si el autoregistro es coherente con los seedRoles (sin roleId desconocido). */
export function isAutoregisterCoherent(
    autoregister: AutoregisterConfigWire | undefined,
    availableSeedRoles: SeedRoleWire[],
): boolean {
    return unknownAutoregisterRoleIds(autoregister, availableSeedRoles).length === 0;
}

/* ───────────────────────────── Sección colapsable ───────────────────────────── */

function CollapsibleSection({
    title,
    summary,
    defaultOpen = false,
    children,
}: {
    title: string;
    /** Texto chico a la derecha del título (estado resumido, ej. "Activado"). */
    summary?: string;
    defaultOpen?: boolean;
    children: ReactNode;
}) {
    const [open, setOpen] = useState(defaultOpen);
    const bodyId = useId();
    return (
        <div className="rounded-lg border bg-muted/20">
            <button
                type="button"
                aria-expanded={open}
                aria-controls={bodyId}
                className="flex w-full items-center justify-between gap-2 px-4 py-3 text-sm font-semibold hover:text-foreground"
                onClick={() => {
                    setOpen((v) => !v);
                }}
            >
                <span className="flex items-center gap-2">
                    {title}
                    {summary && (
                        <span className="text-xs font-normal text-muted-foreground">{summary}</span>
                    )}
                </span>
                <ChevronDown
                    className={`size-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
                />
            </button>
            {open && (
                <div id={bodyId} className="space-y-4 border-t px-4 py-4">
                    {children}
                </div>
            )}
        </div>
    );
}

/* ───────────────────────────── 1. MFA / seguridad ───────────────────────────── */

function SecurityPolicySection({
    value,
    onChange,
    offerable,
    principalIdentifier,
}: {
    value: ApplicationSecurityPolicyDefaultWire;
    onChange: (next: ApplicationSecurityPolicyDefaultWire) => void;
    /** Métodos ofrecibles según el identificador de acceso (derivado en el padre). */
    offerable: MfaMethod[];
    /** Identificador principal (para el invariante anti-lockout del aviso). */
    principalIdentifier: 'EMAIL' | 'PHONE';
}) {
    const policy = value;
    const methods = policy.methodsAllowed ?? [];
    // DEC-RBAC-087: aviso cuando el único OTP quedaría siendo el del secundario (o ninguno).
    const missingUniversal =
        policy.mfaEnabled && !hasUniversalMfaMethod(policy, principalIdentifier);
    const principalLabel = principalIdentifier === 'PHONE' ? 'SMS' : 'correo';

    const patch = (p: Partial<ApplicationSecurityPolicyDefaultWire>): void => {
        onChange({ ...policy, ...p });
    };
    const toggleMethod = (m: MfaMethod, checked: boolean): void => {
        const next = checked ? [...new Set([...methods, m])] : methods.filter((x) => x !== m);
        patch({ methodsAllowed: next });
    };

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-4 rounded-md border p-3">
                <div className="space-y-0.5">
                    <Label htmlFor="def-mfa-enabled" className="cursor-pointer font-medium">
                        Verificación en dos pasos
                    </Label>
                    <p className="text-xs text-muted-foreground">
                        Valor predeterminado para las organizaciones que se creen con esta
                        aplicación.
                    </p>
                </div>
                <Switch
                    id="def-mfa-enabled"
                    checked={policy.mfaEnabled ?? false}
                    onCheckedChange={(checked) => {
                        patch({ mfaEnabled: checked });
                    }}
                    aria-label="Verificación en dos pasos"
                />
            </div>

            <fieldset
                className={
                    policy.mfaEnabled ? 'space-y-4' : 'space-y-4 pointer-events-none opacity-50'
                }
                disabled={!policy.mfaEnabled}
                aria-disabled={!policy.mfaEnabled}
            >
                <legend className="sr-only">Opciones de verificación en dos pasos</legend>

                {/* DEC-RBAC-051: checkbox "Obligar 2FA" (mfaRequired) quitado — campo purgado del
                    contrato (sin enforcement; el login deriva el método del device del usuario). */}

                <div className="space-y-2">
                    <Label>Métodos permitidos</Label>
                    <p className="text-xs text-muted-foreground">
                        Los métodos disponibles dependen del identificador de acceso: el código por
                        el canal del identificador principal siempre está disponible; el del
                        secundario solo si la aplicación permite identificador secundario. La app de
                        autenticación, siempre.
                    </p>
                    {offerable.map((m) => (
                        <div key={m} className="flex items-center gap-3">
                            <Checkbox
                                id={`def-mfa-${m}`}
                                checked={methods.includes(m)}
                                disabled={!policy.mfaEnabled}
                                onCheckedChange={(checked) => {
                                    toggleMethod(m, checked === true);
                                }}
                                className="mt-0.5"
                            />
                            <Label htmlFor={`def-mfa-${m}`} className="cursor-pointer">
                                {mfaMethodLabel(m)}
                            </Label>
                        </div>
                    ))}
                </div>

                {missingUniversal && (
                    <Banner variant="warning">
                        El método del identificador secundario no puede ser el único: no todos los
                        usuarios lo tienen. Permite al menos un método universal — la{' '}
                        <strong>app de autenticación</strong> o el código por{' '}
                        <strong>{principalLabel}</strong> (el del identificador principal).
                    </Banner>
                )}

                {/* DEC-RBAC-051: Select "Método preferido por defecto" (defaultPreferred) quitado —
                    campo purgado del contrato (el login deriva el método preferido del device). */}
            </fieldset>
            {/* DEC-RBAC-048: reglas de contraseña y timeout de sesión NO se configuran por app
                (estándar global) — fuera del armador. */}
        </div>
    );
}

/* ───────────────────── 1.b Identificador de acceso (primary/secondary) ───────────────────── */

// DEC-RBAC-079/081: el identificador principal de login que heredan los tenants + si la app permite
// un secundario verificable (login dual). Inmutable tras crear la app. El enum del wire es mayúscula
// (EMAIL/PHONE); default EMAIL. Anti-id: el Select muestra labels legibles, nunca el enum crudo.
const PRIMARY_IDENTIFIER_OPTIONS: { value: 'EMAIL' | 'PHONE'; label: string }[] = [
    { value: 'EMAIL', label: 'Correo electrónico' },
    { value: 'PHONE', label: 'Teléfono' },
];

function IdentifierSection({
    principalIdentifier,
    allowSecondary,
    onChange,
}: {
    principalIdentifier: 'EMAIL' | 'PHONE';
    allowSecondary: boolean;
    onChange: (p: { principalIdentifier?: 'EMAIL' | 'PHONE'; allowSecondary?: boolean }) => void;
}) {
    return (
        <div className="space-y-4">
            <div className="space-y-1">
                <Select
                    label="Identificador principal"
                    value={principalIdentifier}
                    onChange={(v) => {
                        onChange({ principalIdentifier: v as 'EMAIL' | 'PHONE' });
                    }}
                    options={PRIMARY_IDENTIFIER_OPTIONS}
                />
                <p className="text-xs text-muted-foreground">
                    Con qué dato inician sesión los usuarios de las organizaciones de esta
                    aplicación. Inmutable tras crear la app.
                </p>
            </div>

            <div className="flex items-start justify-between gap-4 rounded-md border p-3">
                <div className="space-y-0.5">
                    <Label htmlFor="def-allow-secondary" className="cursor-pointer font-medium">
                        Permitir identificador secundario
                    </Label>
                    <p className="text-xs text-muted-foreground">
                        Habilita un segundo identificador verificable (login dual). Inmutable tras
                        crear la app.
                    </p>
                </div>
                <Switch
                    id="def-allow-secondary"
                    checked={allowSecondary}
                    onCheckedChange={(checked) => {
                        onChange({ allowSecondary: checked });
                    }}
                    aria-label="Permitir identificador secundario"
                />
            </div>
        </div>
    );
}

/* ───────────────────────── 2. BYOID (externalScopeLevels) ───────────────────────── */

function ExternalScopeLevelsSection({
    levels,
    value,
    onChange,
}: {
    levels: LevelDefLocal[];
    value: Scope[];
    onChange: (next: Scope[]) => void;
}) {
    // Orden alfabético por displayName (default del proyecto).
    const sorted = [...levels].sort((a, b) => a.displayName.localeCompare(b.displayName, 'es'));
    const toggle = (level: Scope, checked: boolean): void => {
        const next = checked ? [...new Set([...value, level])] : value.filter((l) => l !== level);
        onChange(next);
    };

    if (sorted.length === 0) {
        return (
            <p className="text-xs text-muted-foreground">
                La aplicación no tiene niveles definidos. Define niveles primero para marcar cuáles
                traen identificador propio.
            </p>
        );
    }

    return (
        <fieldset className="space-y-3">
            <legend className="sr-only">Niveles con identificador propio</legend>
            <p className="text-xs text-muted-foreground">
                Marca los niveles cuyo identificador lo trae la organización en vez de generarse.
            </p>
            {sorted.map((lvl) => (
                <div key={lvl.level} className="flex items-center gap-3">
                    <Checkbox
                        id={`def-byoid-${lvl.level}`}
                        checked={value.includes(lvl.level)}
                        onCheckedChange={(checked) => {
                            toggle(lvl.level, checked === true);
                        }}
                        className="mt-0.5"
                    />
                    {/* displayName del nivel, nunca el scope crudo (anti-id en UI). */}
                    <Label htmlFor={`def-byoid-${lvl.level}`} className="cursor-pointer">
                        {lvl.displayName}
                    </Label>
                </div>
            ))}
        </fieldset>
    );
}

/* ───────────────────── 3. Campos personalizados (userFieldDefs) ───────────────────── */

const EMPTY_FIELD: UserFieldDefDraft = {
    key: '',
    label: '',
    type: 'text',
    options: [],
    required: false,
    injectToToken: false,
    enabled: true,
};

function UserFieldDefsSection({
    value,
    onChange,
}: {
    value: UserFieldDefDraft[];
    onChange: (next: UserFieldDefDraft[]) => void;
}) {
    // null = cerrado; '' = agregando; string = editando esa key.
    const [editing, setEditing] = useState<string | null>(null);
    const [draft, setDraft] = useState<UserFieldDefDraft>(EMPTY_FIELD);
    const [keyError, setKeyError] = useState<string | undefined>(undefined);

    // Orden alfabético por label (default del proyecto).
    const sorted = [...value].sort((a, b) => a.label.localeCompare(b.label, 'es'));

    const openAdd = (): void => {
        setDraft(EMPTY_FIELD);
        setKeyError(undefined);
        setEditing('');
    };
    const openEdit = (f: UserFieldDefDraft): void => {
        setDraft({ ...f, options: f.options ?? [] });
        setKeyError(undefined);
        setEditing(f.key);
    };
    const cancel = (): void => {
        setEditing(null);
        setKeyError(undefined);
    };

    const save = (): void => {
        const isNew = editing === '';
        const key = draft.key.trim();
        const label = draft.label.trim();
        if (!label) {
            setKeyError('Escribe una etiqueta para el campo.');
            return;
        }
        // La key es inmutable: solo se valida al crear; al editar se preserva la original.
        if (isNew) {
            if (!FIELD_KEY_REGEX.test(key)) {
                setKeyError(
                    'La clave debe empezar con letra y usar solo minúsculas, números y guion bajo (mín. 2).',
                );
                return;
            }
            if (value.some((f) => f.key === key)) {
                setKeyError('Ya existe un campo con esa clave. Elige otra.');
                return;
            }
        }
        const cleanOptions =
            draft.type === 'select'
                ? [...new Set((draft.options ?? []).map((o) => o.trim()).filter(Boolean))].sort(
                      (a, b) => a.localeCompare(b, 'es'),
                  )
                : undefined;
        if (draft.type === 'select' && (!cleanOptions || cleanOptions.length === 0)) {
            setKeyError('Un campo de tipo lista necesita al menos una opción.');
            return;
        }
        const next: UserFieldDefDraft = {
            key: isNew ? key : draft.key,
            label,
            type: draft.type,
            options: cleanOptions,
            required: draft.required,
            injectToToken: draft.injectToToken,
            enabled: draft.enabled,
        };
        onChange(isNew ? [...value, next] : value.map((f) => (f.key === editing ? next : f)));
        setEditing(null);
    };

    const remove = (key: string): void => {
        onChange(value.filter((f) => f.key !== key));
    };

    const addOption = (): void => {
        setDraft((d) => ({ ...d, options: [...(d.options ?? []), ''] }));
    };
    const editOption = (i: number, v: string): void => {
        setDraft((d) => ({
            ...d,
            options: (d.options ?? []).map((o, idx) => (idx === i ? v : o)),
        }));
    };
    const removeOption = (i: number): void => {
        setDraft((d) => ({ ...d, options: (d.options ?? []).filter((_, idx) => idx !== i) }));
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <Label>Campos personalizados</Label>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    leftIcon={<Plus className="h-4 w-4" />}
                    onClick={openAdd}
                >
                    Agregar campo
                </Button>
            </div>

            {sorted.length === 0 && editing === null ? (
                <p className="text-xs text-muted-foreground">
                    Sin campos personalizados. Agrega los que la organización debe completar por
                    usuario.
                </p>
            ) : (
                <ul className="space-y-2">
                    {sorted.map((f) => (
                        <li
                            key={f.key}
                            data-testid={`field-row-${f.key}`}
                            className="flex items-start justify-between gap-2 rounded-md border px-3 py-2"
                        >
                            <div className="min-w-0 space-y-0.5">
                                <div className="flex items-center gap-2">
                                    {/* label legible, no la key cruda (anti-id en UI). */}
                                    <span
                                        data-testid="field-label"
                                        className="text-sm font-medium leading-tight"
                                    >
                                        {f.label}
                                    </span>
                                    <Badge variant="info">
                                        {f.type === 'select' ? 'Lista' : 'Texto'}
                                    </Badge>
                                    {!f.enabled && <Badge variant="warning">Deshabilitado</Badge>}
                                </div>
                                <p className="text-xs text-muted-foreground leading-tight">
                                    {f.required ? 'Obligatorio' : 'Opcional'}
                                    {f.injectToToken ? ' · al token' : ''}
                                </p>
                            </div>
                            <div className="flex shrink-0 items-center gap-1">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    aria-label={`Editar campo ${f.label}`}
                                    onClick={() => {
                                        openEdit(f);
                                    }}
                                >
                                    <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    aria-label={`Quitar campo ${f.label}`}
                                    onClick={() => {
                                        remove(f.key);
                                    }}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {editing !== null && (
                <div className="space-y-3 rounded-md border bg-muted/40 p-3">
                    {editing === '' ? (
                        <div className="space-y-1">
                            <div className="flex items-center gap-1">
                                <Label htmlFor="def-field-key">Clave</Label>
                                <HelpTooltip content="Identificador interno del campo (snake_case, ej. documento_id). Inmutable luego de crear." />
                            </div>
                            <Input
                                id="def-field-key"
                                hint="Identificador interno (minúsculas, números, guion bajo). No se puede cambiar luego."
                                placeholder="documento_id"
                                value={draft.key}
                                error={keyError}
                                onChange={(e) => {
                                    setDraft((d) => ({ ...d, key: e.target.value }));
                                    setKeyError(undefined);
                                }}
                            />
                        </div>
                    ) : (
                        <div className="space-y-1">
                            <Label>Clave (no editable)</Label>
                            <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
                                {draft.key}
                            </p>
                        </div>
                    )}
                    <Input
                        label="Etiqueta"
                        hint="Cómo se ve para el usuario. Ej: Documento de identidad"
                        value={draft.label}
                        error={editing !== '' ? keyError : undefined}
                        onChange={(e) => {
                            setDraft((d) => ({ ...d, label: e.target.value }));
                            setKeyError(undefined);
                        }}
                    />
                    <Select
                        label="Tipo"
                        value={draft.type}
                        onChange={(v) => {
                            setDraft((d) => ({ ...d, type: v as FieldType }));
                        }}
                        options={[
                            { value: 'text', label: 'Texto' },
                            { value: 'select', label: 'Lista de opciones' },
                        ]}
                    />
                    {draft.type === 'select' && (
                        <div className="space-y-2">
                            <Label>Opciones</Label>
                            {(draft.options ?? []).map((o, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <Input
                                        aria-label={`Opción ${String(i + 1)}`}
                                        placeholder={`Opción ${String(i + 1)}`}
                                        value={o}
                                        onChange={(e) => {
                                            editOption(i, e.target.value);
                                        }}
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        aria-label={`Quitar opción ${String(i + 1)}`}
                                        onClick={() => {
                                            removeOption(i);
                                        }}
                                    >
                                        <Trash2 className="size-4" />
                                    </Button>
                                </div>
                            ))}
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                leftIcon={<Plus className="size-4" />}
                                onClick={addOption}
                            >
                                Agregar opción
                            </Button>
                        </div>
                    )}
                    {(
                        [
                            ['required', 'Obligatorio', undefined],
                            [
                                'injectToToken',
                                'Incluir en la sesión',
                                'Si se activa, el valor de este campo se incluye en la sesión del usuario.',
                            ],
                            ['enabled', 'Habilitado', undefined],
                        ] as const
                    ).map(([key, label, help]) => (
                        <div key={key} className="flex items-center gap-3">
                            <Checkbox
                                id={`def-field-${key}`}
                                checked={draft[key]}
                                onCheckedChange={(checked) => {
                                    setDraft((d) => ({ ...d, [key]: checked === true }));
                                }}
                                className="mt-0.5"
                            />
                            <Label htmlFor={`def-field-${key}`} className="cursor-pointer">
                                {label}
                            </Label>
                            {help && <HelpTooltip content={help} />}
                        </div>
                    ))}
                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={cancel}>
                            Cancelar
                        </Button>
                        <Button type="button" size="sm" onClick={save}>
                            Guardar campo
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ───────────────────────── 5. Branding (brandingConfig) ───────────────────────── */

function BrandingSection({
    value,
    onChange,
}: {
    value: TenantBrandingConfigWire;
    onChange: (next: TenantBrandingConfigWire) => void;
}) {
    const patch = (p: Partial<TenantBrandingConfigWire>): void => {
        onChange({ ...value, ...p });
    };
    return (
        <div className="space-y-4">
            <Input
                label="Logo (URL)"
                placeholder="https://…/logo.svg"
                value={value.logoUrl ?? ''}
                onChange={(e) => {
                    patch({ logoUrl: e.target.value || undefined });
                }}
            />
            <Input
                label="Favicon (URL)"
                placeholder="https://…/favicon.ico"
                value={value.faviconUrl ?? ''}
                onChange={(e) => {
                    patch({ faviconUrl: e.target.value || undefined });
                }}
            />
            {/* DEC-RBAC-053: Input "URL base de la webapp" (webappBaseUrl) quitado — campo
                write-only/muerto, purgado del contrato (TenantBrandingConfig). */}
            <Input
                type="number"
                min={1}
                label="Ancho del logo (px)"
                value={value.logoWidth ?? ''}
                onChange={(e) => {
                    patch({
                        logoWidth: e.target.value === '' ? undefined : Number(e.target.value),
                    });
                }}
            />
            {/* Colores ocultos: consistente con el editor de branding de platform (branding = solo
                logo/favicon/URL/tamaño; los colores los fija el diseño editorial). Los valores de color
                existentes se preservan en la config, solo se quita el editor. */}
        </div>
    );
}

/* ───────────────────────── 6. Autoregistro (autoregister) ───────────────────────── */

const AUTOREGISTER_SCOPE_BASE: { value: Scope; label: string }[] = [
    { value: 'TENANT', label: WHOLE_ORGANIZATION },
];

function AutoregisterSection({
    value,
    onChange,
    levels,
    availableSeedRoles,
}: {
    value: AutoregisterConfigWire;
    onChange: (next: AutoregisterConfigWire) => void;
    levels: LevelDefLocal[];
    availableSeedRoles: SeedRoleWire[];
}) {
    const patch = (p: Partial<AutoregisterConfigWire>): void => {
        onChange({ ...value, ...p });
    };

    // Roles ofrecidos por displayName (anti-id), alfabético. roleId queda detrás del label.
    const roleOptions = [...availableSeedRoles]
        .sort((a, b) => a.displayName.localeCompare(b.displayName, 'es'))
        .map((r) => ({ value: r.roleId, label: r.displayName }));
    const roleLabel = (roleId: string): string =>
        availableSeedRoles.find((r) => r.roleId === roleId)?.displayName ?? UNKNOWN_ROLE;

    // Scopes: TENANT + los niveles de la app (por displayName), alfabético por label.
    const scopeOptions = [
        ...AUTOREGISTER_SCOPE_BASE,
        ...levels.map((l) => ({ value: l.level, label: l.displayName })),
    ].sort((a, b) => a.label.localeCompare(b.label, 'es'));
    const scopeLabel = (scope: Scope): string =>
        scopeOptions.find((o) => o.value === scope)?.label ?? OTHER_SCOPE_NAME;

    const unknown = unknownAutoregisterRoleIds(value, availableSeedRoles);

    const addRole = (): void => {
        // Guard explícito: sin seedRoles no se puede agregar un allowedRole coherente (evita roleId vacío).
        // El botón ya está disabled en ese caso; esto hace el invariante explícito.
        if (roleOptions.length === 0) return;
        const next: AutoregisterAllowedRoleWire = { roleId: roleOptions[0].value, scope: 'TENANT' };
        patch({ allowedRoles: [...value.allowedRoles, next] });
    };
    // TD-WEBAPP-026: `allowedScopeRefs` (acotar el rol a refs de scope específicas) NO se edita aquí —
    // editRole hace spread, así que un valor inbound (modo editar) se PRESERVA, pero no hay UI para
    // setearlo/limpiarlo en el armador. Caso base (rol+scope) cubierto; refinamiento diferido.
    const editRole = (i: number, p: Partial<AutoregisterAllowedRoleWire>): void => {
        patch({
            allowedRoles: value.allowedRoles.map((r, idx) => (idx === i ? { ...r, ...p } : r)),
        });
    };
    const removeRole = (i: number): void => {
        patch({ allowedRoles: value.allowedRoles.filter((_, idx) => idx !== i) });
    };

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-4 rounded-md border p-3">
                <div className="space-y-0.5">
                    <Label htmlFor="def-autoreg-enabled" className="cursor-pointer font-medium">
                        Autorregistro
                    </Label>
                    <p className="text-xs text-muted-foreground">
                        Permite que los usuarios se registren solos con uno de los roles permitidos.
                    </p>
                </div>
                <Switch
                    id="def-autoreg-enabled"
                    checked={value.enabled}
                    onCheckedChange={(checked) => {
                        patch({ enabled: checked });
                    }}
                    aria-label="Autorregistro"
                />
            </div>

            <fieldset
                className={value.enabled ? 'space-y-4' : 'space-y-4 pointer-events-none opacity-50'}
                disabled={!value.enabled}
                aria-disabled={!value.enabled}
            >
                <legend className="sr-only">Configuración de autorregistro</legend>

                {value.enabled && unknown.length > 0 && (
                    <Banner variant="warning">
                        Hay {String(unknown.length)} rol(es) permitido(s) que no existen en los
                        roles base de la aplicación. El sistema los rechaza. Corrígelos o quítalos.
                    </Banner>
                )}

                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                            <Label>Roles permitidos</Label>
                            <HelpHint text={HELP_TEXT.role} />
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            leftIcon={<Plus className="h-4 w-4" />}
                            disabled={!value.enabled || roleOptions.length === 0}
                            onClick={addRole}
                        >
                            Agregar rol
                        </Button>
                    </div>
                    {roleOptions.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                            La aplicación no tiene roles base. Define roles base primero.
                        </p>
                    ) : value.allowedRoles.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                            Agrega al menos un rol que pueda usarse en el autorregistro.
                        </p>
                    ) : (
                        <ul className="space-y-2">
                            {value.allowedRoles.map((r, i) => {
                                const isUnknown = unknown.includes(r.roleId);
                                return (
                                    <li
                                        key={`${r.roleId}-${String(i)}`}
                                        data-testid={`autoreg-role-${String(i)}`}
                                        className="grid gap-2 rounded-md border px-3 py-2 sm:grid-cols-[1fr_1fr_auto]"
                                    >
                                        <Select
                                            label="Rol"
                                            value={r.roleId}
                                            error={isUnknown ? 'Rol desconocido' : undefined}
                                            onChange={(v) => {
                                                editRole(i, { roleId: v });
                                            }}
                                            options={roleOptions}
                                        />
                                        <Select
                                            label="Alcance"
                                            value={r.scope}
                                            onChange={(v) => {
                                                editRole(i, { scope: v as Scope });
                                            }}
                                            options={scopeOptions}
                                        />
                                        <div className="flex items-end">
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                aria-label={`Quitar rol ${roleLabel(r.roleId)} (${scopeLabel(r.scope)})`}
                                                onClick={() => {
                                                    removeRole(i);
                                                }}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
                {/* DEC-RBAC-048: el TTL del OTP no se configura por app (estándar global). */}
            </fieldset>
        </div>
    );
}

/* ───────────────────────────────── DefaultsStep ───────────────────────────────── */

interface Props {
    /** Defaults controlados de la app. Todo opcional; cada sección cae a un default sano. */
    value: ApplicationDefaultsDraft;
    /** Se llama con el draft completo actualizado en cada cambio de cualquier sección. */
    onChange: (next: ApplicationDefaultsDraft) => void;
    /** Niveles de la app (para BYOID y para los scopes del autoregistro). Por displayName. */
    levels?: LevelDefLocal[];
    /** Roles seed de la app (del SeedRolesStep del padre): origen de los roleId del autoregistro. */
    availableSeedRoles?: SeedRoleWire[];
}

/**
 * Paso CONTROLADO y PURO de los defaults (plantilla) del armador de aplicaciones (#3-a). No llama a la
 * API ni tiene estado propio del value: recibe `value` (`ApplicationDefaultsDraft`) + `onChange` y los
 * reporta al padre, reusable entre el wizard de CREAR (Task D) y el detalle de EDITAR (Task E). Solo
 * tiene estado de UI local (qué editor inline está abierto, qué sección colapsada).
 *
 * Seis sub-secciones colapsables, cada una con un default sano (DEC-RBAC-047): MFA/seguridad, BYOID,
 * campos personalizados, validación de token, branding y autoregistro. Todo opcional. Anti-id: muestra
 * displayName de niveles/roles, nunca el scope/roleId/key crudo.
 *
 * El gateo DURO de coherencia del autoregistro (roleId ∈ seedRoles) lo hace el padre con
 * `isAutoregisterCoherent` / `unknownAutoregisterRoleIds`; aquí solo se advierte inline.
 */
export function DefaultsStep({ value, onChange, levels = [], availableSeedRoles = [] }: Props) {
    const patch = (p: Partial<ApplicationDefaultsDraft>): void => {
        onChange({ ...value, ...p });
    };

    const security = value.securityPolicy ?? DEFAULT_SECURITY_POLICY;
    const branding = value.brandingConfig ?? {};
    const autoregister = value.autoregister ?? DEFAULT_AUTOREGISTER;
    const externalScopeLevels = value.externalScopeLevels ?? [];
    const userFieldDefs = value.userFieldDefs ?? [];
    const tokenValidationMode = value.tokenValidationMode ?? 'offline';
    // DEC-RBAC-079/081: default EMAIL, secundario apagado.
    const principalIdentifier = value.principalIdentifier ?? 'EMAIL';
    const allowSecondary = value.allowSecondary ?? false;
    // DEC-RBAC-085/087: qué métodos MFA se ofrecen según el identificador de acceso.
    const offerable = offerableMethods(principalIdentifier, allowSecondary);

    // Al cambiar el identificador (principal o secundario), podamos los métodos seleccionados que ya no
    // aplican (ej. pasar a phone-primary sin secundario saca EMAIL). Así methodsAllowed nunca queda con
    // un método no ofrecible (que el backend rechazaría por el invariante anti-lockout).
    const handleIdentifierChange = (p: {
        principalIdentifier?: 'EMAIL' | 'PHONE';
        allowSecondary?: boolean;
    }): void => {
        const nextPrincipal = p.principalIdentifier ?? principalIdentifier;
        const nextAllowSecondary = p.allowSecondary ?? allowSecondary;
        const nextOfferable = offerableMethods(nextPrincipal, nextAllowSecondary);
        const prunedMethods = (security.methodsAllowed ?? []).filter((m) =>
            nextOfferable.includes(m),
        );
        onChange({
            ...value,
            ...p,
            securityPolicy: { ...security, methodsAllowed: prunedMethods },
        });
    };

    return (
        <div className="space-y-3">
            {/* Identificador PRIMERO: define qué métodos MFA se pueden ofrecer abajo. */}
            <CollapsibleSection
                title="Identificador de acceso"
                summary={principalIdentifier === 'PHONE' ? 'Teléfono' : 'Correo electrónico'}
                defaultOpen
            >
                <IdentifierSection
                    principalIdentifier={principalIdentifier}
                    allowSecondary={allowSecondary}
                    onChange={handleIdentifierChange}
                />
            </CollapsibleSection>

            <CollapsibleSection title="Seguridad y MFA" defaultOpen>
                <SecurityPolicySection
                    value={security}
                    offerable={offerable}
                    principalIdentifier={principalIdentifier}
                    onChange={(securityPolicy) => {
                        patch({ securityPolicy });
                    }}
                />
            </CollapsibleSection>

            <CollapsibleSection
                title="Niveles con identificador propio"
                summary={
                    externalScopeLevels.length > 0
                        ? `${String(externalScopeLevels.length)} nivel(es)`
                        : undefined
                }
            >
                <ExternalScopeLevelsSection
                    levels={levels}
                    value={externalScopeLevels}
                    onChange={(next) => {
                        patch({ externalScopeLevels: next });
                    }}
                />
            </CollapsibleSection>

            <CollapsibleSection
                title="Campos personalizados"
                summary={
                    userFieldDefs.length > 0
                        ? `${String(userFieldDefs.length)} campo(s)`
                        : undefined
                }
            >
                <UserFieldDefsSection
                    value={userFieldDefs}
                    onChange={(next) => {
                        patch({ userFieldDefs: next });
                    }}
                />
            </CollapsibleSection>

            <CollapsibleSection title="Validación de la sesión">
                <Select
                    label="Modo de validación de la sesión"
                    value={tokenValidationMode}
                    onChange={(v) => {
                        patch({ tokenValidationMode: v as TokenValidationMode });
                    }}
                    options={TOKEN_VALIDATION_MODES.map((m) => ({
                        value: m,
                        label: TOKEN_VALIDATION_MODE_META[m].label,
                    }))}
                />
            </CollapsibleSection>

            <CollapsibleSection title="Marca">
                <BrandingSection
                    value={branding}
                    onChange={(brandingConfig) => {
                        patch({ brandingConfig });
                    }}
                />
            </CollapsibleSection>

            <CollapsibleSection
                title="Autorregistro"
                summary={autoregister.enabled ? 'Activado' : undefined}
            >
                <AutoregisterSection
                    value={autoregister}
                    onChange={(next) => {
                        patch({ autoregister: next });
                    }}
                    levels={levels}
                    availableSeedRoles={availableSeedRoles}
                />
            </CollapsibleSection>
        </div>
    );
}
